























































































import Vue from "vue";
import { RawLocation } from "vue-router";
import BidModel, { ItemState } from "@/models/BidModel";
import filters from "@/global/filters";

const DESCRIPTION_LIMIT = 140;

export default Vue.extend({
	name: "AuctionItemCard",

	props: {
		item: {
			type: Object as () => ItemState,
			required: true,
		},
		isFavorite: {
			type: Boolean,
			default: false,
		},
		auctionOpen: {
			type: Boolean,
			default: true,
		},
		canBid: {
			type: Boolean,
			default: true,
		},
		loginLink: {
			type: [Object, String] as unknown as () => RawLocation,
			default: "/login",
		},
	},

	data() {
		return {
			customAmount: null as number | null,
			isBidding: false as boolean,
			error: "" as string,
			showModal: false as boolean,
		};
	},

	computed: {
		// The amount the Bid button will place: the typed value, or the
		// minimum next bid when the field is empty. Drives the button label.
		effectiveBid(): number {
			const amt = this.customAmount;
			if (amt !== null && !isNaN(amt) && amt > 0) return amt;
			return this.item.minimumNextBid;
		},

		// Blurred backdrop reuses the item image. The URL is quoted because
		// filenames can contain spaces — an unquoted CSS url() would be invalid
		// and silently dropped by the browser.
		backdropStyle(): { [key: string]: string } {
			return this.item.imageUrl ? { backgroundImage: `url("${this.item.imageUrl}")` } : {};
		},

		isLong(): boolean {
			return !!this.item.description && this.item.description.length > DESCRIPTION_LIMIT;
		},

		displayDescription(): string {
			const description = this.item.description || "";
			if (!this.isLong) return description;
			return description.slice(0, DESCRIPTION_LIMIT).replace(/\s+\S*$/, "") + "…";
		},

		showBadge(): boolean {
			// Always show the badge once the viewer has a stake in the item.
			if (this.item.youAreLeading || this.item.youHaveBid) return true;
			// Otherwise only show it when the item has no bids at all.
			return this.item.bidCount === 0;
		},

		statusLabel(): string {
			if (this.item.youAreLeading) return this.auctionOpen ? "Winning" : "Won";
			if (this.item.youHaveBid) return this.auctionOpen ? "Losing" : "Lost";
			return this.auctionOpen ? "No bids" : "Closed";
		},

		badgeClass(): string {
			if (this.item.youAreLeading) return "bg-success";
			if (this.item.youHaveBid) return "bg-danger";
			return "bg-primary";
		},

		statusClass(): string {
			if (this.item.youAreLeading) return "auction-card--leading";
			if (this.item.youHaveBid) return "auction-card--outbid";
			return "";
		},
	},

	mounted() {
		window.addEventListener("keydown", this.onKeydown);
	},

	beforeDestroy() {
		window.removeEventListener("keydown", this.onKeydown);
	},

	methods: {
		currency(value: number | null): string {
			return filters.currency(value || 0);
		},

		openModal() {
			this.showModal = true;
		},

		closeModal() {
			this.showModal = false;
		},

		onKeydown(e: KeyboardEvent) {
			if (e.key === "Escape" && this.showModal) this.closeModal();
		},

		async submit(amount: number) {
			this.isBidding = true;
			this.error = "";

			try {
				const result = await BidModel.place(this.item.itemNumber, amount);

				if (result.accepted) {
					this.customAmount = null;
					this.$emit("bid-accepted", this.item.itemNumber);
					this.$emit("placed", this.item.itemNumber);
				} else if (result.reason === "too_low") {
					this.error = "Too low — the minimum is now " + this.currency(result.minimumNextBid || 0) + ".";
					this.$emit("placed", this.item.itemNumber);
				} else if (result.reason === "already_leading") {
					this.error = "You're already the top bidder on this item.";
					this.$emit("placed", this.item.itemNumber);
				} else if (result.reason === "auction_closed") {
					this.error = "Bidding has closed for this auction.";
					this.$emit("placed", this.item.itemNumber);
				} else if (result.reason === "invalid_session") {
					this.$emit("session-expired");
				} else {
					this.error = "That bid didn't go through. Please try again.";
				}
			} catch (e) {
				this.error = "Network error placing your bid. Please try again.";
			}

			this.isBidding = false;
		},
	},
});
